import { Icons8SearchResponse, icons8Search } from 'libs/e1api/icons8';
import clonedeep from 'lodash.clonedeep';
import { loadIcons8Icon } from 'modules/Editor/components/WidgetMenu/SideMenu/IconWidgetMenu/IconWidgetMenu.helpers';
import { WidgetId } from 'types/idTypes';
import { InfographState } from 'types/infographTypes';
import { WidgetType } from 'types/widget.types';
import { isTest } from 'utils/environment';
import { computeSvgViewBox } from 'utils/svg';
import { IconWidgetData } from 'widgets/IconWidget/IconWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

/**
 * Replace icons in an infograph with icons from icons8
 * Replaceable icons are specified by replaceables array.
 * Uses `iconKeywords` to search for icons to replace with.
 *
 * @param infograph Infograph to update
 * @param replaceables Icon widgets to replace
 * @param iconKeywords Keywords to search for icons
 * @returns InfographState
 */
export const replaceIcons = async (infograph: InfographState, replaceables: WidgetId[], iconKeywords: string[]) => {
  // There was an issue generating keywords for the icons or
  // The template does not have any icons to replace
  if (!iconKeywords?.length || !replaceables?.length) {
    return infograph;
  }

  const updatedInfograph = clonedeep(infograph);
  const { widgets: widgetMap } = updatedInfograph;

  // Get one icon for each replaceable widget
  const icons8List = await getIcons(replaceables.length, iconKeywords);

  // Replace icons with icons retrieved from icons8
  for (let i = 0; i < replaceables.length; i++) {
    const widgetId = replaceables[i];
    if (getWidgetTypeFromId(widgetId) !== WidgetType.Icon) {
      continue;
    }

    const widget = widgetMap[widgetId] as IconWidgetData;
    if (!widget) {
      continue;
    }

    // No more icons in list, stop replacing
    if (!icons8List[i]) {
      break;
    }

    // Load icon svg and replace icon
    // Dimensions will be updated so that it will be contained within the widget so that there is no overlap
    const { iconPath, id, iconSvg } = icons8List[i];
    if (!iconSvg) {
      continue;
    }

    const viewBox = computeSvgViewBox(iconSvg);
    const { widthPx: oldWidthPx, heightPx: oldHeightPx, topPx: oldTopPx, leftPx: oldLeftPx, rotateDeg } = widget;

    const {
      widthPx: newWidthPx,
      heightPx: newHeightPx,
      topPx: newTopPx,
      leftPx: newLeftPx,
    } = computeNewWidgetDimension(
      {
        widthPx: oldWidthPx,
        heightPx: oldHeightPx,
        topPx: oldTopPx,
        leftPx: oldLeftPx,
        rotateDeg,
      },
      viewBox,
    );

    widgetMap[widgetId] = {
      ...widget,
      iconId: id,
      iconMetadata: {
        isMono: true,
        iconPath,
        viewBox,
      },

      // Update dimensions so widget has correct bounding box
      widthPx: newWidthPx,
      heightPx: newHeightPx,
      topPx: newTopPx,
      leftPx: newLeftPx,
    };
  }

  return updatedInfograph as InfographState;
};

type WidgetDimension = {
  widthPx: number;
  heightPx: number;
  leftPx: number;
  topPx: number;
  rotateDeg: number;
};

/**
 * Compute new widget dimensions so that the icon will fit within the widget
 * The new icon will expand to fit either width or height of the widget and will be centered
 *
 * @param widgetDimension
 * @param viewBox
 * @returns
 */
export const computeNewWidgetDimension = (widgetDimension: WidgetDimension, viewBox: string) => {
  const { widthPx, heightPx, leftPx, topPx } = widgetDimension;

  const [, , svgWidth, svgHeight] = viewBox.split(' ').map((view) => +view);
  const oldAspectRatio = widthPx / heightPx;
  const newAspectRatio = svgWidth / svgHeight;

  let newHeightPx = heightPx;
  let newWidthPx = widthPx;
  let newLeftPx = leftPx;
  let newTopPx = topPx;
  if (oldAspectRatio > newAspectRatio) {
    // height should expand to fit
    newWidthPx = newHeightPx * newAspectRatio;
    newLeftPx = leftPx + (widthPx - newWidthPx) / 2;
  } else if (oldAspectRatio < newAspectRatio) {
    // width should expand to fit
    newHeightPx = newWidthPx / newAspectRatio;
    newTopPx = topPx + (heightPx - newHeightPx) / 2;
  }

  return {
    widthPx: newWidthPx,
    heightPx: newHeightPx,
    leftPx: newLeftPx,
    topPx: newTopPx,
  };
};

type GetIconsFn = (
  numIcons: number,
  iconKeywords: string[],
) => Promise<{ id: string; iconPath: string; iconSvg: string }[]>;

/**
 * Get a list of Icons8 icons to use for replace
 *
 * @param numIcons Number of icons to get
 * @param iconKeywords Keywords to use for search
 * @returns Array of icons { id: string, iconPath: string }
 */
export const getIcons: GetIconsFn = async (numIcons, iconKeywords) => {
  const NUM_PARALLEL_REQUESTS = 4;

  const icons: {
    id: string;
    iconPath: string;
    iconSvg: string;
  }[] = [];

  const icons8SearchCache: { [query: string]: Icons8SearchResponse } = {};

  let promiseList: Promise<void>[] = [];

  // Filter out 'card' keyword - OpenAI includes it in the results for majority of prompts
  // but icons returned from Icons8 are often irrelevant
  const keywords = iconKeywords.filter((keyword) => keyword.toLowerCase() !== 'card');

  // Perform Icons8 search for icon keyword at keywords[idx];
  // Will go through list of keywords until an icon is found
  async function performIconSearch(idx: number) {
    const MAX_RETRIES = 5;

    let tryCount = 0;
    while (tryCount < MAX_RETRIES && tryCount < keywords.length) {
      const query = keywords[(idx + tryCount) % keywords.length];
      const icons8Result =
        icons8SearchCache[query] || (await icons8Search({ query, limit: 10, offset: 0, isColor: false }));

      // Add to cache
      icons8SearchCache[query] = icons8Result;

      // No icons returned for this keyword
      if (!icons8Result?.success || !icons8Result?.icons?.length) {
        if (!isTest) {
          console.warn('Couldnt find icon for', query);
        }
        tryCount++;
        continue;
      }

      // Found icon - add to icons list result
      const iconIdx = Math.floor(Math.random() * icons8Result.icons.length);
      const { commonName, platform, sourceFormat, id } = icons8Result.icons[iconIdx] || icons8Result.icons[0];

      const iconPath = `${platform}/${commonName}.${sourceFormat}`;
      const iconResult = await loadIcons8Icon(id);
      const iconSvg = iconResult?.icon?.svg;
      if (!iconSvg) {
        tryCount++;
        continue;
      }

      icons.push({
        id,
        iconPath,
        iconSvg,
      });

      return;
    }
  }

  // Perform icon searches in parallel
  for (let i = 0; i < numIcons; i++) {
    promiseList.push(performIconSearch(i));
    if (promiseList.length >= NUM_PARALLEL_REQUESTS) {
      await Promise.all(promiseList);
      promiseList = [];
    }
  }

  await Promise.all(promiseList);
  return icons;
};
